import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../utils/api';

export default function SchoolDashboard() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'enquiries' | 'offers' | 'profile' | 'messages'>('enquiries');

  // Data state
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [schoolProfile, setSchoolProfile] = useState<any>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState<any>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    summary: '',
    day_fees: '',
    boarding_fees: '',
    age_min: '',
    age_max: '',
    curriculum: 'British',
    type: 'Independent',
    gender: 'Co-educational',
    specialisms: '',
    postcode: '',
    address_line1: '',
    city: '',
    country: 'UK',
    urn: '',
    website: '',
    application_deadline: '',
    entry_requirements: '',
    application_process: '',
    scholarships: '',
    registration_fees: '',
    tours_info: '',
    main_entry_points: [] as number[],
    inspection_highlights: [''] as string[],
    academic_achievements: {
      gcse_percent: '',
      alevel_percent: '',
      oxbridge_percent: '',
      teacher_pupil_ratio: ''
    },
    extra_curricular: {
      sport: '',
      music: '',
      clubs: ''
    },
    facilities_list: [{title: '', description: ''}] as {title: string, description: string}[],
    subject_offerings: [] as string[],
    inspection_rating: '',
    inspection_date: '',
  });

  // Offer form state
  const [offerForm, setOfferForm] = useState({
    enquiryId: '',
    childId: '',
    yearGroup: '',
    fee: '',
    scholarshipAmount: '',
    message: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'enquiries') loadEnquiries();
    if (activeTab === 'offers') loadOffers();
    if (activeTab === 'messages') loadMessages();
    if (activeTab === 'profile' && !schoolProfile) loadProfile();
  }, [activeTab]);

  async function loadInitialData() {
    try {
      await loadEnquiries();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadEnquiries() {
    try {
      const res = await api.getEnquiries();
      setEnquiries(res.enquiries || []);
    } catch (error) {
      console.error('Failed to load enquiries:', error);
    }
  }

  async function loadOffers() {
    try {
      const res = await api.getOffers();
      setOffers(res.offers || []);
    } catch (error) {
      console.error('Failed to load offers:', error);
    }
  }

  async function loadMessages() {
    try {
      const res = await api.getMessages();
      setMessages(res.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }

  async function loadProfile() {
    try {
      const res = await api.getMySchool();
      if (res.school) {
        const profile = res.school;
        setSchoolProfile(profile);
        const parseJSON = (str: string | null) => {
          if (!str) return null;
          try { return JSON.parse(str); } catch { return null; }
        };

        setProfileForm({
          name: profile.name || '',
          description: profile.description || '',
          summary: profile.summary || '',
          day_fees: profile.day_fees?.toString() || '',
          boarding_fees: profile.boarding_fees?.toString() || '',
          age_min: profile.age_min?.toString() || '',
          age_max: profile.age_max?.toString() || '',
          curriculum: profile.curriculum || 'British',
          type: profile.type || 'Independent',
          gender: profile.gender || 'Co-educational',
          specialisms: profile.specialisms || '',
          postcode: profile.postcode || '',
          address_line1: profile.address_line1 || '',
          city: profile.city || '',
          country: profile.country || 'UK',
          urn: profile.urn || '',
          website: profile.website || '',
          application_deadline: profile.application_deadline || '',
          entry_requirements: profile.entry_requirements || '',
          application_process: profile.application_process || '',
          scholarships: profile.scholarships || '',
          registration_fees: profile.registration_fees || '',
          tours_info: profile.tours_info || '',
          main_entry_points: parseJSON(profile.main_entry_points) || [],
          inspection_highlights: parseJSON(profile.inspection_highlights) || [''],
          academic_achievements: parseJSON(profile.academic_achievements) || {
            gcse_percent: '', alevel_percent: '', oxbridge_percent: '', teacher_pupil_ratio: ''
          },
          extra_curricular: parseJSON(profile.extra_curricular) || {
            sport: '', music: '', clubs: ''
          },
          facilities_list: parseJSON(profile.facilities_list) || [{title: '', description: ''}],
          subject_offerings: parseJSON(profile.subject_offerings) || [],
          inspection_rating: profile.inspection_rating || '',
          inspection_date: profile.inspection_date || '',
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      showToast('Failed to load school profile', 'error');
    }
  }

  async function handleUpdateEnquiryStatus(enquiryId: string, status: string) {
    try {
      setActionLoading(enquiryId);
      await api.updateEnquiryStatus(enquiryId, status);
      showToast(`Enquiry marked as ${status}`, 'success');
      await loadEnquiries();
    } catch (error) {
      showToast('Failed to update enquiry', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSubmitOffer(e: React.FormEvent) {
    e.preventDefault();
    try {
      setActionLoading('offer');
      await api.createOffer({
        enquiry_id: offerForm.enquiryId,
        child_id: offerForm.childId,
        year_group: offerForm.yearGroup,
        fee: parseInt(offerForm.fee),
        scholarship_amount: parseInt(offerForm.scholarshipAmount) || 0,
        message: offerForm.message,
      });
      showToast('Offer sent successfully', 'success');
      setShowOfferModal(false);
      setOfferForm({ enquiryId: '', childId: '', yearGroup: '', fee: '', scholarshipAmount: '', message: '' });
      await loadOffers();
      // Update enquiry status to responded
      if (selectedEnquiry) {
        await handleUpdateEnquiryStatus(selectedEnquiry.id, 'responded');
      }
    } catch (error) {
      showToast('Failed to send offer', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    try {
      setActionLoading('profile');
      if (schoolProfile?.id) {
        await api.updateSchool(schoolProfile.id, {
          name: profileForm.name,
          description: profileForm.description,
          summary: profileForm.summary,
          day_fees: parseInt(profileForm.day_fees) || null,
          boarding_fees: parseInt(profileForm.boarding_fees) || null,
          age_min: parseInt(profileForm.age_min) || null,
          age_max: parseInt(profileForm.age_max) || null,
          curriculum: profileForm.curriculum,
          type: profileForm.type,
          gender: profileForm.gender,
          specialisms: profileForm.specialisms,
          postcode: profileForm.postcode,
          address_line1: profileForm.address_line1,
          city: profileForm.city,
          country: profileForm.country,
          urn: profileForm.urn,
          website: profileForm.website,
          application_deadline: profileForm.application_deadline,
          entry_requirements: profileForm.entry_requirements,
          application_process: profileForm.application_process,
          scholarships: profileForm.scholarships,
          registration_fees: profileForm.registration_fees,
          tours_info: profileForm.tours_info,
          main_entry_points: JSON.stringify(profileForm.main_entry_points),
          inspection_highlights: JSON.stringify(profileForm.inspection_highlights.filter(h => h.trim())),
          academic_achievements: JSON.stringify(profileForm.academic_achievements),
          extra_curricular: JSON.stringify(profileForm.extra_curricular),
          facilities_list: JSON.stringify(profileForm.facilities_list.filter(f => f.title.trim())),
          subject_offerings: JSON.stringify(profileForm.subject_offerings),
          inspection_rating: profileForm.inspection_rating,
          inspection_date: profileForm.inspection_date,
        });
        showToast('Profile updated successfully', 'success');
        await loadProfile();
      }
    } catch (error) {
      showToast('Failed to update profile', 'error');
    } finally {
      setActionLoading(null);
    }
  }

  function openOfferModal(enquiry: any) {
    setSelectedEnquiry(enquiry);
    setOfferForm({
      ...offerForm,
      enquiryId: enquiry.id,
      childId: enquiry.child_id,
    });
    setShowOfferModal(true);
  }

  const tabs = [
    { id: 'enquiries', label: 'Enquiries', icon: '📧', count: enquiries.filter(e => e.status === 'sent' || e.status === 'viewed').length },
    { id: 'offers', label: 'Offers', icon: '🎓', count: offers.length },
    { id: 'messages', label: 'Messages', icon: '💬', count: messages.reduce((sum, m) => sum + (m.unread_count || 0), 0) },
    { id: 'profile', label: 'School Profile', icon: '🏫' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">IndieSchools - School Portal</h1>
              <p className="text-sm text-gray-600">Welcome, {schoolProfile?.name || user?.email}</p>
            </div>
            <button onClick={logout} className="btn btn-secondary">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Verification Status Banner */}
        {schoolProfile && (
          <div className="mb-6">
            {!schoolProfile.contract_signed && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Contract Required:</strong> You must sign the platform terms and conditions before your school can receive enquiries.
                      <button onClick={() => setActiveTab('profile')} className="ml-2 underline font-medium">
                        Go to Profile
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            )}
            {schoolProfile.contract_signed && !schoolProfile.verified && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Pending Verification:</strong> Your school profile is under review. We are verifying your URN and website. You will be able to receive enquiries once verified.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {schoolProfile.verified && schoolProfile.contract_signed && (
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      <strong>Verified:</strong> Your school is verified and can now receive enquiries from parents.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* ENQUIRIES TAB */}
        {activeTab === 'enquiries' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Parent Enquiries</h2>
              <div className="text-sm text-gray-600">
                {enquiries.length} {enquiries.length === 1 ? 'enquiry' : 'enquiries'}
              </div>
            </div>
            {enquiries.length > 0 ? (
              <div className="space-y-4">
                {enquiries.map(enquiry => (
                  <div key={enquiry.id} className="card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{enquiry.parent_name}</h3>
                          <span className={`badge badge-${
                            enquiry.status === 'sent' ? 'secondary' :
                            enquiry.status === 'viewed' ? 'primary' :
                            enquiry.status === 'responded' ? 'success' : 'secondary'
                          }`}>
                            {enquiry.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          For: {enquiry.child_name} (Age {enquiry.child_age}) • {new Date(enquiry.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700 mb-3">{enquiry.message}</p>
                        <div className="flex gap-2">
                          {enquiry.status === 'sent' && (
                            <button
                              onClick={() => handleUpdateEnquiryStatus(enquiry.id, 'viewed')}
                              disabled={actionLoading === enquiry.id}
                              className="btn btn-primary"
                            >
                              Mark as Viewed
                            </button>
                          )}
                          {(enquiry.status === 'sent' || enquiry.status === 'viewed') && (
                            <>
                              <button
                                onClick={() => openOfferModal(enquiry)}
                                className="btn btn-success"
                              >
                                Make Offer
                              </button>
                              <button
                                onClick={() => handleUpdateEnquiryStatus(enquiry.id, 'responded')}
                                disabled={actionLoading === enquiry.id}
                                className="btn btn-secondary"
                              >
                                Mark as Responded
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No enquiries received yet</p>
              </div>
            )}
          </div>
        )}

        {/* OFFERS TAB */}
        {activeTab === 'offers' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Offers Sent</h2>
              <div className="text-sm text-gray-600">
                {offers.length} {offers.length === 1 ? 'offer' : 'offers'}
              </div>
            </div>
            {offers.length > 0 ? (
              <div className="space-y-4">
                {offers.map(offer => (
                  <div key={offer.id} className="card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{offer.parent_name}</h3>
                          <span className={`badge badge-${
                            offer.status === 'pending' ? 'warning' :
                            offer.status === 'accepted' ? 'success' :
                            offer.status === 'declined' ? 'danger' : 'secondary'
                          }`}>
                            {offer.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          For: {offer.child_name} • Year {offer.year_group} • {new Date(offer.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700 mb-3">{offer.message}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-semibold text-primary-600">
                            £{offer.fee?.toLocaleString()}/year
                          </span>
                          {offer.scholarship_amount > 0 && (
                            <span className="text-sm text-green-600">
                              Scholarship: £{offer.scholarship_amount.toLocaleString()}
                            </span>
                          )}
                          {offer.status === 'accepted' && offer.confirmed_years > 0 && (
                            <span className="text-sm text-green-600">
                              ✓ Active ({offer.confirmed_years} {offer.confirmed_years === 1 ? 'year' : 'years'})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">You haven't sent any offers yet</p>
                <button
                  onClick={() => setActiveTab('enquiries')}
                  className="btn btn-primary"
                >
                  View Enquiries
                </button>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Messages</h2>
            {messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map(thread => (
                  <div key={thread.thread_id} className="card hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{thread.parent_name || thread.other_party}</h3>
                          {thread.unread_count > 0 && (
                            <span className="badge badge-danger">{thread.unread_count} new</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {new Date(thread.last_message_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2">{thread.last_message}</p>
                      </div>
                      <button className="btn btn-primary ml-4">View</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No messages yet</p>
              </div>
            )}
          </div>
        )}

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">School Profile</h2>
            <form onSubmit={handleSaveProfile} className="card max-w-3xl">
              <div className="space-y-4">
                <div>
                  <label className="label">School Name</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="Enter school name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="label">Summary (1-2 sentences)</label>
                  <textarea
                    className="input w-full"
                    rows={2}
                    placeholder="Brief summary for search results"
                    value={profileForm.summary}
                    onChange={(e) => setProfileForm({...profileForm, summary: e.target.value})}
                  />
                </div>
                <div>
                  <label className="label">Full Description</label>
                  <textarea
                    className="input w-full"
                    rows={6}
                    placeholder="Detailed school description"
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({...profileForm, description: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">School Type</label>
                    <select
                      className="input w-full"
                      value={profileForm.type}
                      onChange={(e) => setProfileForm({...profileForm, type: e.target.value})}
                    >
                      <option>Independent</option>
                      <option>Day</option>
                      <option>Boarding</option>
                      <option>Prep</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Gender</label>
                    <select
                      className="input w-full"
                      value={profileForm.gender}
                      onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                    >
                      <option>Co-educational</option>
                      <option>Boys</option>
                      <option>Girls</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Day Fees (£/year)</label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="15000"
                      value={profileForm.day_fees}
                      onChange={(e) => setProfileForm({...profileForm, day_fees: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="label">Boarding Fees (£/year)</label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="35000"
                      value={profileForm.boarding_fees}
                      onChange={(e) => setProfileForm({...profileForm, boarding_fees: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Age Range Min</label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="4"
                      value={profileForm.age_min}
                      onChange={(e) => setProfileForm({...profileForm, age_min: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="label">Age Range Max</label>
                    <input
                      type="number"
                      className="input w-full"
                      placeholder="18"
                      value={profileForm.age_max}
                      onChange={(e) => setProfileForm({...profileForm, age_max: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Curriculum</label>
                  <select
                    className="input w-full"
                    value={profileForm.curriculum}
                    onChange={(e) => setProfileForm({...profileForm, curriculum: e.target.value})}
                  >
                    <option>British</option>
                    <option>IB</option>
                    <option>Montessori</option>
                    <option>Waldorf</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="label">Specialisms (comma separated)</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="Arts, Sports, STEM"
                    value={profileForm.specialisms}
                    onChange={(e) => setProfileForm({...profileForm, specialisms: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">URN (Unique Reference Number)</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="Enter school URN"
                      value={profileForm.urn}
                      onChange={(e) => setProfileForm({...profileForm, urn: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">Required for verification</p>
                  </div>
                  <div>
                    <label className="label">Website</label>
                    <input
                      type="url"
                      className="input w-full"
                      placeholder="https://www.yourschool.co.uk"
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({...profileForm, website: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">Required for verification</p>
                  </div>
                </div>
                <div>
                  <label className="label">Address</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="Street address"
                    value={profileForm.address_line1}
                    onChange={(e) => setProfileForm({...profileForm, address_line1: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">City</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="London"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="label">Postcode</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="SW1A 1AA"
                      value={profileForm.postcode}
                      onChange={(e) => setProfileForm({...profileForm, postcode: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="label">Country</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="UK"
                      value={profileForm.country}
                      onChange={(e) => setProfileForm({...profileForm, country: e.target.value})}
                    />
                  </div>
                </div>

                {/* Admissions Information Section */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Admissions Information</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This information helps parents understand your admissions process. All enquiries will go through the IndieSchools platform.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="label">Application Deadline</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 31st January 2025 for September 2025 entry"
                        value={profileForm.application_deadline}
                        onChange={(e) => setProfileForm({...profileForm, application_deadline: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">Entry Requirements</label>
                      <textarea
                        className="input w-full"
                        rows={3}
                        placeholder="Describe what's required for entry (e.g., entrance exams, interviews, assessments)"
                        value={profileForm.entry_requirements}
                        onChange={(e) => setProfileForm({...profileForm, entry_requirements: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">Application Process</label>
                      <textarea
                        className="input w-full"
                        rows={4}
                        placeholder="Outline the step-by-step application process parents should follow"
                        value={profileForm.application_process}
                        onChange={(e) => setProfileForm({...profileForm, application_process: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">Scholarships & Bursaries</label>
                      <textarea
                        className="input w-full"
                        rows={3}
                        placeholder="Describe available scholarships, bursaries, and financial aid options"
                        value={profileForm.scholarships}
                        onChange={(e) => setProfileForm({...profileForm, scholarships: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">Registration Fees</label>
                      <textarea
                        className="input w-full"
                        rows={2}
                        placeholder="Outline registration fees and when they are payable"
                        value={profileForm.registration_fees}
                        onChange={(e) => setProfileForm({...profileForm, registration_fees: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">School Tours & Visits</label>
                      <textarea
                        className="input w-full"
                        rows={3}
                        placeholder="Explain how parents can arrange tours or visits (through the platform only)"
                        value={profileForm.tours_info}
                        onChange={(e) => setProfileForm({...profileForm, tours_info: e.target.value})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Parents will contact you through IndieSchools to arrange visits</p>
                    </div>
                  </div>
                </div>

                {/* Main Entry Points */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Main Entry Points</h3>
                  <p className="text-sm text-gray-600 mb-4">Select the typical ages when students can join your school</p>
                  <div className="flex flex-wrap gap-2">
                    {[7, 11, 13, 16].map(age => (
                      <label key={age} className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={profileForm.main_entry_points.includes(age)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setProfileForm({...profileForm, main_entry_points: [...profileForm.main_entry_points, age].sort()});
                            } else {
                              setProfileForm({...profileForm, main_entry_points: profileForm.main_entry_points.filter(a => a !== age)});
                            }
                          }}
                        />
                        <span>Age {age}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Overview Section - Inspection & Achievements */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">School Overview</h3>

                  <div className="mb-6">
                    <label className="label">Inspection Highlights</label>
                    <p className="text-xs text-gray-500 mb-2">Key strengths from your latest inspection report</p>
                    {profileForm.inspection_highlights.map((highlight, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          className="input w-full"
                          placeholder="e.g., Pupils achieve exceptional academic results"
                          value={highlight}
                          onChange={(e) => {
                            const updated = [...profileForm.inspection_highlights];
                            updated[index] = e.target.value;
                            setProfileForm({...profileForm, inspection_highlights: updated});
                          }}
                        />
                        {index === profileForm.inspection_highlights.length - 1 && (
                          <button
                            type="button"
                            onClick={() => setProfileForm({...profileForm, inspection_highlights: [...profileForm.inspection_highlights, '']})}
                            className="btn btn-secondary whitespace-nowrap"
                          >
                            + Add
                          </button>
                        )}
                        {profileForm.inspection_highlights.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setProfileForm({...profileForm, inspection_highlights: profileForm.inspection_highlights.filter((_, i) => i !== index)})}
                            className="btn btn-danger"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="label">GCSE Grades 9-7 (A*-A) %</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 98%"
                        value={profileForm.academic_achievements.gcse_percent}
                        onChange={(e) => setProfileForm({...profileForm, academic_achievements: {...profileForm.academic_achievements, gcse_percent: e.target.value}})}
                      />
                    </div>
                    <div>
                      <label className="label">A-Level Grades A*-B %</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 95%"
                        value={profileForm.academic_achievements.alevel_percent}
                        onChange={(e) => setProfileForm({...profileForm, academic_achievements: {...profileForm.academic_achievements, alevel_percent: e.target.value}})}
                      />
                    </div>
                    <div>
                      <label className="label">Oxbridge Leavers %</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 18%"
                        value={profileForm.academic_achievements.oxbridge_percent}
                        onChange={(e) => setProfileForm({...profileForm, academic_achievements: {...profileForm.academic_achievements, oxbridge_percent: e.target.value}})}
                      />
                    </div>
                    <div>
                      <label className="label">Teacher to Pupil Ratio</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 1:8"
                        value={profileForm.academic_achievements.teacher_pupil_ratio}
                        onChange={(e) => setProfileForm({...profileForm, academic_achievements: {...profileForm.academic_achievements, teacher_pupil_ratio: e.target.value}})}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="label">Sport Activities</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., Rugby, Football, Cricket, Rowing, Tennis, Athletics"
                        value={profileForm.extra_curricular.sport}
                        onChange={(e) => setProfileForm({...profileForm, extra_curricular: {...profileForm.extra_curricular, sport: e.target.value}})}
                      />
                    </div>
                    <div>
                      <label className="label">Music Activities</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., Orchestra, Choir, Jazz Band, Chamber Music"
                        value={profileForm.extra_curricular.music}
                        onChange={(e) => setProfileForm({...profileForm, extra_curricular: {...profileForm.extra_curricular, music: e.target.value}})}
                      />
                    </div>
                    <div>
                      <label className="label">Clubs & Societies</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., Debating, Model UN, Chess, Robotics, Coding"
                        value={profileForm.extra_curricular.clubs}
                        onChange={(e) => setProfileForm({...profileForm, extra_curricular: {...profileForm.extra_curricular, clubs: e.target.value}})}
                      />
                    </div>
                  </div>
                </div>

                {/* Facilities Section */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Facilities</h3>
                  <p className="text-xs text-gray-500 mb-4">Add details about your school's facilities</p>
                  {profileForm.facilities_list.map((facility, index) => (
                    <div key={index} className="mb-4 p-4 border rounded-lg">
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          className="input w-full font-semibold"
                          placeholder="Facility name (e.g., Science Centre)"
                          value={facility.title}
                          onChange={(e) => {
                            const updated = [...profileForm.facilities_list];
                            updated[index].title = e.target.value;
                            setProfileForm({...profileForm, facilities_list: updated});
                          }}
                        />
                        {profileForm.facilities_list.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setProfileForm({...profileForm, facilities_list: profileForm.facilities_list.filter((_, i) => i !== index)})}
                            className="btn btn-danger"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <textarea
                        className="input w-full"
                        rows={2}
                        placeholder="Description (e.g., Purpose-built facility with 18 laboratories)"
                        value={facility.description}
                        onChange={(e) => {
                          const updated = [...profileForm.facilities_list];
                          updated[index].description = e.target.value;
                          setProfileForm({...profileForm, facilities_list: updated});
                        }}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setProfileForm({...profileForm, facilities_list: [...profileForm.facilities_list, {title: '', description: ''}]})}
                    className="btn btn-secondary"
                  >
                    + Add Facility
                  </button>
                </div>

                {/* Reviews & Inspection */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">External Reviews & Inspection</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Inspection Rating</label>
                      <select
                        className="input w-full"
                        value={profileForm.inspection_rating}
                        onChange={(e) => setProfileForm({...profileForm, inspection_rating: e.target.value})}
                      >
                        <option value="">Select rating...</option>
                        <option value="ISI: Excellent">ISI: Excellent</option>
                        <option value="ISI: Good">ISI: Good</option>
                        <option value="Ofsted: Outstanding">Ofsted: Outstanding</option>
                        <option value="Ofsted: Good">Ofsted: Good</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Last Inspected</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., 2023"
                        value={profileForm.inspection_date}
                        onChange={(e) => setProfileForm({...profileForm, inspection_date: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Subject Offerings */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Subject Offerings</h3>
                  <p className="text-sm text-gray-600 mb-4">Select the subjects your school offers</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      'English Literature and Language',
                      'Mathematics',
                      'Biology',
                      'Chemistry',
                      'Physics',
                      'French',
                      'German',
                      'Spanish',
                      'Mandarin',
                      'Latin',
                      'Greek',
                      'History',
                      'Geography',
                      'Religious Studies',
                      'Art',
                      'Music',
                      'Drama',
                      'Design & Technology',
                      'Computer Science',
                      'Physical Education'
                    ].map(subject => (
                      <label key={subject} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={profileForm.subject_offerings.includes(subject)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setProfileForm({...profileForm, subject_offerings: [...profileForm.subject_offerings, subject]});
                            } else {
                              setProfileForm({...profileForm, subject_offerings: profileForm.subject_offerings.filter(s => s !== subject)});
                            }
                          }}
                        />
                        <span className="text-sm">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={actionLoading === 'profile'}
                    className="btn btn-primary"
                  >
                    {actionLoading === 'profile' ? 'Saving...' : 'Save Changes'}
                  </button>
                  {schoolProfile?.id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/schools/${schoolProfile.id}`)}
                      className="btn btn-secondary"
                    >
                      👁️ Preview Profile
                    </button>
                  )}
                </div>
              </div>
            </form>

            {/* Contract Signing Section */}
            {schoolProfile && !schoolProfile.contract_signed && (
              <div className="card max-w-3xl mt-6">
                <h3 className="text-xl font-bold mb-4">Platform Terms & Conditions</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                  <h4 className="font-semibold mb-2">Terms of Service for Schools</h4>
                  <div className="text-sm text-gray-700 space-y-2">
                    <p><strong>1. Lead Fees</strong></p>
                    <ul className="list-disc list-inside ml-4">
                      <li>First-year placement: 20% of annual tuition fees</li>
                      <li>Ongoing years: 5% of annual tuition fees</li>
                      <li>Fees are invoiced upon student enrollment confirmation</li>
                    </ul>

                    <p><strong>2. School Verification</strong></p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Schools must provide a valid URN (Unique Reference Number)</li>
                      <li>Website verification is required</li>
                      <li>Schools will not appear in search results or receive leads until verified</li>
                    </ul>

                    <p><strong>3. Payment Terms</strong></p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Invoices are due within 30 days of issue</li>
                      <li>Late payments may result in suspension of service</li>
                      <li>All fees are non-refundable once student enrollment is confirmed</li>
                    </ul>

                    <p><strong>4. Professional Conduct</strong></p>
                    <ul className="list-disc list-inside ml-4">
                      <li>All communications must be professional and respectful</li>
                      <li>Schools may not request direct contact information from parents</li>
                      <li>All enquiries must be handled through the platform</li>
                    </ul>

                    <p><strong>5. Data Protection</strong></p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Schools must comply with GDPR and data protection laws</li>
                      <li>Parent and child data must be handled securely</li>
                      <li>Data must not be shared with third parties</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start mb-4">
                  <input
                    type="checkbox"
                    id="accept-terms"
                    className="mt-1"
                  />
                  <label htmlFor="accept-terms" className="ml-2 text-sm text-gray-700">
                    I confirm that I have read and agree to the IndieSchools Terms & Conditions, including the payment criteria outlined above.
                    I understand that my school must be verified before receiving enquiries.
                  </label>
                </div>

                <button
                  onClick={async () => {
                    const checkbox = document.getElementById('accept-terms') as HTMLInputElement;
                    if (!checkbox?.checked) {
                      showToast('Please accept the terms and conditions', 'error');
                      return;
                    }
                    try {
                      await api.signContract(true);
                      showToast('Contract signed successfully! Your profile is now pending verification.', 'success');
                      await loadProfile();
                    } catch (error) {
                      showToast('Failed to sign contract', 'error');
                    }
                  }}
                  className="btn btn-primary"
                >
                  Sign Contract & Submit for Verification
                </button>
              </div>
            )}

            {/* Already Signed Message */}
            {schoolProfile && schoolProfile.contract_signed && (
              <div className="card max-w-3xl mt-6 bg-green-50 border-green-200">
                <div className="flex items-center text-green-800">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Contract Signed</span>
                  {schoolProfile.contract_signed_at && (
                    <span className="ml-2 text-sm">
                      on {new Date(schoolProfile.contract_signed_at * 1000).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Offer Modal */}
      {showOfferModal && selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4">
              Make Offer to {selectedEnquiry.parent_name}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              For: {selectedEnquiry.child_name} (Age {selectedEnquiry.child_age})
            </p>
            <form onSubmit={handleSubmitOffer} className="space-y-4">
              <div>
                <label className="label">Year Group</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. Year 5, Reception"
                  value={offerForm.yearGroup}
                  onChange={(e) => setOfferForm({ ...offerForm, yearGroup: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Annual Fee (£)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="12000"
                    value={offerForm.fee}
                    onChange={(e) => setOfferForm({ ...offerForm, fee: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Scholarship Amount (£)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="0"
                    value={offerForm.scholarshipAmount}
                    onChange={(e) => setOfferForm({ ...offerForm, scholarshipAmount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="label">Message</label>
                <textarea
                  className="input w-full"
                  rows={6}
                  placeholder="Welcome message and offer details..."
                  value={offerForm.message}
                  onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                  required
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={actionLoading === 'offer'}
                  className="btn btn-primary flex-1"
                >
                  {actionLoading === 'offer' ? 'Sending...' : 'Send Offer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowOfferModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
